import { ElementsModule } from '@modules/elements/elements.module';
import { ChangeDetectionStrategy, Component, effect, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuHeadingComponent } from '@frontendlib/components/features/popover/components/context-menu-heading/context-menu-heading.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { DatePicker2Component } from '@modules/form-controls/components/date-picker-2/date-picker-2.component';
import { dateRangeChanges, setDateRangeSilently } from '@modules/common/utils/date-range-filter.util';
import { type FormsWorkspaceStore } from '../../services/forms-workspace.store';

/** Boards' native list menu adapted to the Forms server's typed filters and stable ordering. */
@Component({
  selector: 'app-forms-library-options',
  templateUrl: './forms-library-options.component.html',
  styleUrls: ['../../../notes/components/notes-list-context-menu/notes-list-context-menu.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    ContextMenuItemComponent,
    ContextMenuDividerComponent,
    ContextMenuHeadingComponent,
    DatePicker2Component,
    DateFormatPipe,
    Button2Component,
    ElementsModule,
  ],
})
export class FormsLibraryOptionsComponent {
  /** Set by the opening directive on the created instance before first render (`ContextMenuDirective` assigns
   * component fields directly, so this cannot be a signal input). */
  public listStore: FormsWorkspaceStore;
  public folders = false;

  protected readonly fromDate = new FormControl<Date | null>(null);
  protected readonly toDate = new FormControl<Date | null>(null);
  protected readonly dateButtonSelected = signal<'from' | 'to'>('from');

  constructor() {
    effect(() => {
      const { from, to } = this.listStore.options();
      setDateRangeSilently(this.fromDate, this.toDate, { from, to });
    });

    dateRangeChanges(this.fromDate, this.toDate)
      .pipe(takeUntilDestroyed())
      .subscribe(({ from, to }) => {
        const options = this.listStore.options();
        if (from?.getTime() !== options.from?.getTime() || to?.getTime() !== options.to?.getTime()) {
          this.listStore.setDateRange(from, to);
        }
      });
  }

  protected clearSelectedDate(): void {
    this.listStore.clearDateRange();
  }
}
